import { Money, PaginatedResponse, Status, StatusQueryParam } from './common';
import { BrandingTheme } from './brandingThemes';
import { UserInfo } from './user';
import { PublicCompany } from './companies';

export enum TeammateRole {
  ADMIN = 0,
  ADVISOR = 1,
  SUPPORTER = 2,
}

export type PublicTeammate = {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  company: PublicCompany;
  active: boolean;
};

export type TeammateItem = {
  uuid: string;
  fullName: string;
  avatar: string | null;
  email: string;
  role: TeammateRole;
  status: Status;
  companyName: string;
  totalActiveBankAccounts: number;
  totalAvailableBalance: Money;
};

export type TeammateAggregateInfo = {
  totalCompanies: number;
  totalActiveBankAccounts: number;
  totalAdmins: number;
  totalAdvisors: number;
  totalSupporters: number;
};

export type TeammateListResponse = {
  teammates: PaginatedResponse<TeammateItem>;
  aggregateInfo: TeammateAggregateInfo;
};

export type TeammateListQueryParams = {
  page: number;
  company_id?: string;
  status?: Status;
  records_per_page?: number;
};

export type TeammateForSelectionQueryParams = {
  company_id?: string;
  status?: StatusQueryParam;
  roles?: TeammateRole[];
};

export type TeammateForSelectItem = {
  uuid: string;
  firstName: string;
  lastName: string;
  fullName: string;
};

export type TeammateUser = {
  uuid: string;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  cellPhoneNumber: string;
  businessPhoneNumber: string;
  title: string;
  bio: string;
  avatar: string;
  dob: string | null;
  onlineStatus: boolean;
  isSandbox: number;
  lastLogin: string;
  deactivatedDate: string | null;
  reactivatedDate: string | null;
  isLocked: boolean;
};

export type TeammateCompany = {
  uuid: string;
  name: string;
  companyLoginUrl?: string | null;
  brandingTheme: BrandingTheme;
};

export type TeammateMember = {
  uuid: string;
  user: TeammateUser;
  company: TeammateCompany;
  rateSavingInterest?: number;
  debitCardProgramId?: string | null;
};

export type TeammateAggregateNumbers = {
  customers: number;
  businesses: number;
  bankAccounts: number;
  draftBankApplications: number;
};

export type TeammateDetails = {
  uuid: string;
  user: TeammateUser;
  branchOfficeName: string;
  role: TeammateRole;
  status: number;
  company: TeammateCompany;
  members: TeammateMember[];
  createdAt: string;
  updatedAt: string;
  aggNumbers: TeammateAggregateNumbers;
  deactivatedByUser: UserInfo;
  reactivatedByUser: string | null;
  advisorUrl: string;
  alias: string;
  companyUrl: string;
};

export const queryKeys = {
  list: ['teammatesList'] as const,
  selectionList: ['teammatesSelectionList'] as const,
  details: ['teammateDetails'] as const,
  preflight: ['teammatePreflight'] as const,
};

export type ReactivateTeammateFields = {
  uuid: string;
};

export type TeammateFormFields = {
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  role: TeammateRole;
  title: string;
  cellPhoneNumber: string;
  businessPhoneNumber: string;
  companyId: string;
  branchOfficeName?: string;
  bio?: string;
  members: string[];
  avatarUrl?: string;
  avatarDisplayName: string;
  alias?: string;
};
